import React, { useCallback, useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { DailyProvider, DailyAudio, useDaily, useParticipantIds, useLocalSessionId, useDailyEvent, useVideoTrack, useDevices } from "@daily-co/daily-react";
import { Mic, MicOff, PhoneOff, Video, VideoOff, Loader2, AlertCircle, Settings2, X, Maximize2, Minimize2 } from "lucide-react";

// "fullscreen" = fixed overlay (legacy / mobile expand); "docked" = fills the workspace stage.
const FRAME = {
  fullscreen: "fixed inset-0 z-50",
  docked: "relative w-full h-full min-h-0 flex-1",
};
import { api } from "@/lib/api";
import { toast } from "sonner";
import PreCallCheck from "@shared/consultation/PreCallCheck";
import { describeDeviceError } from "@shared/consultation/deviceErrors";
import { DeviceSelect } from "@shared/consultation/DevicePicker";
import { CallBarTimer } from "@shared/consultation/SessionTimer";

export { describeDeviceError };

/**
 * Daily.co-powered call screen. Replaces the old mock InCallScreen.
 * Props: consultationId, type ('audio'|'video'), onEnd
 */
export default function DailyCall({ consultationId, type, lawyer, onEnd, onJoined, timer, onExpired, canGift = false, onGift, layout = "fullscreen" }) {
  const [expanded, setExpanded] = useState(false);
  const mode = layout === "docked" && expanded ? "fullscreen" : layout;
  const frame = FRAME[mode];
  const expandProps = layout === "docked" ? { expanded, onToggleExpand: () => setExpanded((x) => !x) } : {};
  const [callObject, setCallObject] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [devices, setDevices] = useState(null); // { mic, cam, speaker } chosen in the pre-call check

  // Fetch token once + create call object once — only after the pre-call device check passes
  useEffect(() => {
    if (!checked) return undefined;
    let mounted = true;
    let co = null;
    (async () => {
      try {
        const res = await api.post(`/consultations/${consultationId}/call-token`);
        if (!mounted) return;
        setTokenData(res.data);
        co = DailyIframe.createCallObject();
        setCallObject(co);
        await co.join({
          url: res.data.room_url,
          token: res.data.token,
          videoSource: type === "video" ? (devices?.cam || true) : false,
          audioSource: devices?.mic || true,
          userName: "You",
        });
        if (!mounted) return;
        // A resolved join() IS the successful join — report it here, not from a listener that
        // CallScreen only subscribes after this point (the "joined-meeting" event has already fired).
        onJoined?.();
        if (devices?.speaker && co.setOutputDeviceAsync) {
          co.setOutputDeviceAsync({ outputDeviceId: devices.speaker }).catch(() => {});
        }
        setLoading(false);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Could not start call";
        setError(typeof msg === "string" ? msg : "Could not start call");
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (co) {
        co.leave().catch(() => {});
        co.destroy().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId, checked]);

  if (!checked) {
    return <PreCallCheck type={type} lawyer={lawyer} layout={mode} onReady={(sel) => { setDevices(sel || null); setChecked(true); }} onCancel={onEnd} />;
  }

  if (error) {
    return (
      <div className={`${frame} bg-[#0A2540]/95 text-white grid place-items-center p-6`} data-testid="call-error-overlay">
        <div className="max-w-sm text-center">
          <AlertCircle size={48} className="mx-auto text-amber-400" />
          <h2 className="mt-4 text-xl font-extrabold" style={{ fontFamily: "Poppins" }}>Couldn't start call</h2>
          <p className="mt-2 text-sm text-white/80">{error}</p>
          <button
            onClick={onEnd}
            data-testid="call-error-close"
            className="mt-6 h-11 px-6 rounded-full adv-btn-gold font-bold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (loading || !callObject) {
    return (
      <div className={`${frame} bg-[#0A2540] text-white grid place-items-center`} data-testid="call-loading">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto animate-spin text-[#D4AF37]" />
          <p className="mt-4 text-sm text-white/70">Connecting you now…</p>
        </div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      {/* Call-object mode never plays remote audio by itself — DailyAudio owns the <audio> elements. */}
      <DailyAudio data-testid="daily-audio" />
      <CallScreen type={type} lawyer={lawyer} isAudioOnly={tokenData?.is_audio_only} onEnd={onEnd} timer={timer} onExpired={onExpired} canGift={canGift} onGift={onGift} frame={frame} {...expandProps} />
    </DailyProvider>
  );
}

function CallScreen({ type, lawyer, isAudioOnly, onEnd, timer, onExpired, canGift, onGift, frame = FRAME.fullscreen, expanded = false, onToggleExpand }) {
  const daily = useDaily();
  const participantIds = useParticipantIds({ filter: "remote" });
  const localId = useLocalSessionId();
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(isAudioOnly);
  const [status, setStatus] = useState("Connecting…");
  const [deviceError, setDeviceError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useDailyEvent("joined-meeting", () => setStatus("Waiting for the other participant…"));
  useDailyEvent("participant-joined", () => setStatus("Connected"));
  useDailyEvent("participant-left", () => setStatus("Other participant left"));
  useDailyEvent("error", (e) => toast.error(e?.errorMsg || "Call error"));
  // Mic/camera permission denied, device missing or busy — the other side would hear silence.
  useDailyEvent("camera-error", (e) => {
    const msg = describeDeviceError(e?.error, type);
    setDeviceError(msg);
    toast.error(msg);
  });

  // Sync actual daily state for mute/video
  useEffect(() => {
    if (!daily) return;
    daily.setLocalAudio(!muted);
  }, [daily, muted]);
  useEffect(() => {
    if (!daily || isAudioOnly) return;
    daily.setLocalVideo(!videoOff);
  }, [daily, videoOff, isAudioOnly]);

  const remoteId = participantIds[0];
  const connected = !!remoteId;

  const hangup = async () => {
    try { await daily?.leave(); } catch (e) { console.warn("[daily] leave() failed during hangup", e); }
    onEnd?.();
  };

  const kind = type === "video" && !isAudioOnly ? "Video" : "Audio";
  return (
    <div className={`${frame} bg-[#0A2540] text-white flex flex-col`} data-testid="in-call-screen" data-layout={expanded ? "fullscreen" : frame === FRAME.docked ? "docked" : "fullscreen"}>
      {/* Top bar — the ONE consultation timer (server-authoritative) lives here; Daily's local elapsed clock is gone. */}
      <div className="box-border flex w-full max-w-full min-w-0 flex-wrap items-center justify-between gap-2 bg-black/30 px-4 py-3 sm:px-5 sm:gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2 w-2 rounded-full shrink-0 ${connected ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
          <span className="text-xs font-bold tracking-wider uppercase truncate">
            {connected ? `${kind} · Connected` : status}
          </span>
        </div>
        <CallBarTimer kind={kind} timer={timer} onExpired={onExpired} canGift={canGift} onGift={onGift} />
      </div>

      {deviceError && (
        <div className="mx-4 mt-3 rounded-xl bg-red-500/15 border border-red-400/40 px-4 py-3 flex items-start gap-3" data-testid="call-device-error">
          <AlertCircle size={18} className="text-red-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-100">The other side can't hear you</p>
            <p className="text-xs text-red-100/80 mt-0.5">{deviceError}</p>
          </div>
          <button type="button" onClick={hangup} data-testid="call-device-error-rejoin" className="text-xs font-bold underline text-red-100 shrink-0">
            Leave &amp; rejoin
          </button>
        </div>
      )}

      {/* Main video area — min-h-0 + overflow-hidden so the remote video's intrinsic size
          can never push the controls below the fold; the <video> fills it absolutely. */}
      <div className="flex-1 min-h-0 relative overflow-hidden grid place-items-center" data-testid="call-stage">
        {(!connected || isAudioOnly || type === "audio") ? (
          <div className="text-center p-6">
            <div className="h-40 w-40 sm:h-48 sm:w-48 mx-auto rounded-full bg-[#122F4F] grid place-items-center">
              {lawyer?.lawyer_photo ? (
                <img src={lawyer.lawyer_photo} alt="" className="h-full w-full rounded-full object-cover" />
              ) : (
                <span className="text-5xl text-[#D4AF37] font-bold">
                  {(lawyer?.lawyer_name || "L").split(" ").slice(-2).map((x) => x[0]).join("")}
                </span>
              )}
            </div>
            <h2 className="mt-6 text-2xl font-extrabold" style={{ fontFamily: "Poppins" }}>
              {lawyer?.lawyer_name || "Your lawyer"}
            </h2>
            {!connected && <p className="text-white/60 mt-2 text-sm">{status}</p>}
          </div>
        ) : (
          <RemoteVideo sessionId={remoteId} />
        )}

        {settingsOpen && <InCallDevicePanel video={type === "video" && !isAudioOnly} onClose={() => setSettingsOpen(false)} />}

        {/* PiP self-view (video mode only) */}
        {!isAudioOnly && type === "video" && localId && !videoOff && (
          <SelfViewPip sessionId={localId} />
        )}
      </div>

      {/* Controls — always visible */}
      <div className="box-border flex w-full max-w-full min-w-0 shrink-0 items-center justify-center gap-1.5 bg-black/30 px-3 py-4 sm:gap-4 sm:px-6 sm:py-6" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} data-testid="call-controls">
        <button
          onClick={() => setMuted((x) => !x)}
          data-testid="mute-btn"
          title={muted ? "Unmute" : "Mute"}
          className={`h-11 w-11 sm:h-14 sm:w-14 shrink-0 rounded-full grid place-items-center transition-colors ${muted ? "bg-red-500/90 hover:bg-red-600" : "bg-white/10 hover:bg-white/20"}`}
        >
          {muted ? <MicOff size={22} /> : <Mic size={22} />}
        </button>
        {!isAudioOnly && type === "video" && (
          <button
            onClick={() => setVideoOff((x) => !x)}
            data-testid="video-toggle-btn"
            title={videoOff ? "Turn on camera" : "Turn off camera"}
            className={`h-11 w-11 sm:h-14 sm:w-14 shrink-0 rounded-full grid place-items-center transition-colors ${videoOff ? "bg-red-500/90 hover:bg-red-600" : "bg-white/10 hover:bg-white/20"}`}
          >
            {videoOff ? <VideoOff size={22} /> : <Video size={22} />}
          </button>
        )}
        <button
          onClick={() => setSettingsOpen((x) => !x)}
          data-testid="devices-btn"
          title="Microphone, camera & speaker"
          className={`h-11 w-11 sm:h-14 sm:w-14 shrink-0 rounded-full grid place-items-center transition-colors ${settingsOpen ? "bg-[#D4AF37] text-[#0A2540]" : "bg-white/10 hover:bg-white/20"}`}
        >
          <Settings2 size={22} />
        </button>
        {onToggleExpand && (
          <button
            onClick={onToggleExpand}
            data-testid="expand-call-btn"
            title={expanded ? "Back to workspace" : "Expand call"}
            className="h-11 w-11 sm:h-14 sm:w-14 shrink-0 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 transition-colors"
          >
            {expanded ? <Minimize2 size={22} /> : <Maximize2 size={22} />}
          </button>
        )}
        <button
          onClick={hangup}
          data-testid="end-call-btn"
          title="End call"
          className="h-12 w-12 sm:h-16 sm:w-16 shrink-0 rounded-full bg-red-600 hover:bg-red-700 grid place-items-center shadow-xl"
        >
          <PhoneOff size={26} />
        </button>
      </div>
    </div>
  );
}

// Self-view: sized to the camera's real aspect ratio (portrait on phones) and draggable
// anywhere inside the stage, like Zoom/Meet. Position is kept for the whole call.
export function SelfViewPip({ sessionId }) {
  const [aspect, setAspect] = useState(16 / 9);
  const [pos, setPos] = useState(null); // null → default bottom-right corner
  const pipRef = useRef(null);
  const dragRef = useRef(null);
  const portrait = aspect < 1;

  const clamp = useCallback((x, y) => {
    const pip = pipRef.current;
    const stage = pip?.parentElement;
    if (!pip || !stage) return { x, y };
    const maxX = Math.max(0, stage.clientWidth - pip.offsetWidth);
    const maxY = Math.max(0, stage.clientHeight - pip.offsetHeight);
    return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
  }, []);

  const onPointerDown = (e) => {
    const pip = pipRef.current;
    if (!pip) return;
    const stageRect = pip.parentElement.getBoundingClientRect();
    const rect = pip.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, sx: stageRect.left, sy: stageRect.top };
    pip.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    e.preventDefault();
    setPos(clamp(e.clientX - d.sx - d.dx, e.clientY - d.sy - d.dy));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    pipRef.current?.releasePointerCapture?.(e.pointerId);
  };

  // Keep the PiP inside the stage when the window is resized/rotated.
  useEffect(() => {
    if (!pos) return undefined;
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos, clamp]);

  const style = pos
    ? { left: pos.x, top: pos.y, aspectRatio: `${aspect}` }
    : { right: 12, bottom: 12, aspectRatio: `${aspect}` };
  return (
    <div
      ref={pipRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ ...style, touchAction: "none" }}
      className={`absolute ${portrait ? "w-20 sm:w-28" : "w-28 sm:w-40"} rounded-xl overflow-hidden border-2 border-white/20 bg-black shadow-lg cursor-grab active:cursor-grabbing select-none`}
      data-testid="self-video-pip"
      data-orientation={portrait ? "portrait" : "landscape"}
      data-dragged={pos ? "true" : "false"}
    >
      <LocalVideo sessionId={sessionId} onAspect={setAspect} />
    </div>
  );
}

function LocalVideo({ sessionId, onAspect }) {
  const videoRef = useRef(null);
  const videoState = useVideoTrack(sessionId);
  useEffect(() => {
    const track = videoState?.persistentTrack;
    if (!videoRef.current) return;
    videoRef.current.srcObject = track ? new MediaStream([track]) : null;
  }, [videoState?.persistentTrack]);
  const report = (e) => {
    const { videoWidth: w, videoHeight: h } = e.currentTarget;
    if (w && h) onAspect?.(w / h);
  };
  return (
    <video ref={videoRef} autoPlay muted playsInline onLoadedMetadata={report} onResize={report}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-testid="local-video" />
  );
}

function RemoteVideo({ sessionId }) {
  const videoRef = useRef(null);
  const videoState = useVideoTrack(sessionId);
  useEffect(() => {
    const track = videoState?.persistentTrack;
    if (!videoRef.current) return;
    videoRef.current.srcObject = track ? new MediaStream([track]) : null;
  }, [videoState?.persistentTrack]);
  return <video ref={videoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-contain bg-black" data-testid="remote-video" />;
}

// In-call device switcher — daily-react tracks devices and swaps tracks live.
function InCallDevicePanel({ video, onClose }) {
  const { microphones, cameras, speakers, setMicrophone, setCamera, setSpeaker } = useDevices();
  const toList = (arr) => (arr || []).map((d) => ({ deviceId: d.device.deviceId, label: d.device.label }));
  const selectedId = (arr) => (arr || []).find((d) => d.selected)?.device.deviceId || "";
  const speakerList = toList(speakers);
  return (
    <div className="absolute inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-80 rounded-2xl bg-[#0A2540]/95 backdrop-blur border border-white/15 p-4 shadow-2xl z-10" data-testid="incall-device-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold">Devices</p>
        <button type="button" onClick={onClose} data-testid="incall-device-panel-close" className="h-7 w-7 rounded-full grid place-items-center bg-white/10 hover:bg-white/20"><X size={14} /></button>
      </div>
      <div className="grid gap-3">
        <DeviceSelect kind="mic" devices={toList(microphones)} value={selectedId(microphones)} onChange={setMicrophone} />
        {video && <DeviceSelect kind="cam" devices={toList(cameras)} value={selectedId(cameras)} onChange={setCamera} />}
        {speakerList.length ? (
          <DeviceSelect kind="speaker" devices={speakerList} value={selectedId(speakers)} onChange={setSpeaker} />
        ) : (
          <p className="text-[11px] text-white/50" data-testid="incall-speaker-unsupported">Speaker selection isn't supported by this browser — use your system sound settings.</p>
        )}
      </div>
    </div>
  );
}
